import type { LoopState, PermanentState } from "../types"
import { formatDay } from "../game/calendarEngine"
import { getKnownSchedule, type ScheduledEvent } from "../game/scheduleEngine"
import { getRitualDeadlineDay } from "../game/loopEngine"
import { getEventSpriteStyle } from "../game/sprites"

interface ScheduleScreenProps {
  permanent: PermanentState
  loop: LoopState
  onBack: () => void
}

const STATUS_LABEL: Record<ScheduledEvent["status"], string> = {
  completed: "Happened",
  missed: "Passed",
  prevented: "Prevented",
  today: "Today",
  upcoming: "Coming",
}

/**
 * Whether the player can still get there in time, in plain words. This is the
 * arithmetic the game was previously making the player do by hand: days until
 * the event, minus days of road between here and there.
 */
function describeInterception(entry: ScheduledEvent): {
  text: string
  className: string
} {
  if (entry.status === "completed") {
    return { text: "Already resolved this loop.", className: "intercept-done" }
  }
  if (entry.status === "prevented") {
    return { text: "You have already stopped this from happening.", className: "intercept-done" }
  }
  if (entry.status === "missed") {
    return { text: "The day passed without it.", className: "intercept-done" }
  }
  if (entry.alreadyThere) {
    return { text: "You are standing here.", className: "intercept-good" }
  }
  if (entry.travelDays === null) {
    return {
      text: "No open road from here — a border is closed, or you have not found the way.",
      className: "intercept-bad",
    }
  }
  const road = `${entry.travelDays} day${entry.travelDays === 1 ? "" : "s"} of road`
  if (entry.daysToSpare === null || entry.daysToSpare < 0) {
    const short = Math.abs(entry.daysToSpare ?? 0)
    return {
      text: `${road} — you would arrive ${short} day${short === 1 ? "" : "s"} late.`,
      className: "intercept-bad",
    }
  }
  if (entry.daysToSpare === 0) {
    return { text: `${road} — you would arrive on the day itself.`, className: "intercept-tight" }
  }
  return {
    text: `${road} — you would arrive with ${entry.daysToSpare} day${
      entry.daysToSpare === 1 ? "" : "s"
    } to spare.`,
    className: "intercept-good",
  }
}

export function ScheduleScreen({ permanent, loop, onBack }: ScheduleScreenProps) {
  const schedule = getKnownSchedule(permanent, loop)

  return (
    <div className="screen schedule-screen">
      <h1>Schedule</h1>
      <p className="journal-intro">
        Everything I know is coming, and whether I can still reach it. Today is{" "}
        <strong>{formatDay(loop.currentDay)}</strong>.
      </p>

      {schedule.length === 0 ? (
        <p>
          I know of nothing that is coming. Foreknowledge is earned the hard way — by
          living through a day once, and remembering it after I fall.
        </p>
      ) : (
        <ol className="schedule-list">
          {schedule.map((entry) => {
            const interception = describeInterception(entry)
            const isPast =
              entry.status === "completed" ||
              entry.status === "missed" ||
              entry.status === "prevented"
            // The scene that awaits at this event, drawn from events.png. A place
            // whose event has no art yet simply shows no picture.
            const sceneStyle = getEventSpriteStyle(entry.event.id)
            return (
              <li
                key={entry.event.id}
                className={["schedule-row", `schedule-${entry.status}`].join(" ")}
              >
                <div className="schedule-when">
                  <span className="schedule-day">{formatDay(entry.event.day)}</span>
                  <span className="schedule-status">{STATUS_LABEL[entry.status]}</span>
                  {!isPast && entry.daysUntil > 0 && (
                    <span className="schedule-countdown">
                      in {entry.daysUntil} day{entry.daysUntil === 1 ? "" : "s"}
                    </span>
                  )}
                  {sceneStyle && (
                    <span className="schedule-event-image" style={sceneStyle} aria-hidden="true" />
                  )}
                </div>
                <div className="schedule-what">
                  <h3>{entry.journalEntry?.title ?? entry.locationName}</h3>
                  <p className="schedule-where">{entry.locationName}</p>
                  {entry.journalEntry && (
                    <p className="schedule-detail">{entry.journalEntry.text}</p>
                  )}
                  <p className={`schedule-intercept ${interception.className}`}>
                    {interception.text}
                  </p>
                </div>
              </li>
            )
          })}
        </ol>
      )}

      {permanent.deadlineRevealed && (
        <p className="schedule-deadline">
          The ritual completes on {formatDay(getRitualDeadlineDay(loop.loopNumber))} —{" "}
          {Math.max(0, getRitualDeadlineDay(loop.loopNumber) - loop.currentDay)} days from
          today.
        </p>
      )}

      <button type="button" onClick={onBack}>
        Back to Map
      </button>
    </div>
  )
}
